/**
 * Encuesta interna para centros — /opinion-centros
 *
 * - Página privada (noindex + sin enlaces desde Home/Footer).
 * - Diseñada para compartir por WhatsApp/email a centros.
 * - Tono cercano ("Cuéntanos qué opinas").
 * - Al enviar → thank-you page con estadísticas del sitio, prensa, planes.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import MediaCoverage from '../components/MediaCoverage';
import {
  Heart, CheckCircle2, Users, Newspaper, Mail, TrendingUp,
  Loader2, Star, ArrowRight, Sparkles, Shield, Phone
} from 'lucide-react';
import { API } from '../utils/api';

const REGIONES_CL = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', "O'Higgins", 'Maule', 'Ñuble',
  'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes'
];

const CENTER_PLANS = [
  { id: 'destacado', name: 'Destacado', price: 99000, position: 'Top 11-20' },
  { id: 'premium', name: 'Premium', price: 199000, position: 'Top 4-10', popular: true },
  { id: 'elite', name: 'Elite', price: 350000, position: 'Top 1-3' },
];

// ————————————————————————————————————————
// Componente reusable: input radio grande estilo card
// ————————————————————————————————————————
function RadioCard({ name, value, current, onChange, label, hint, testId, tone = 'emerald' }) {
  const selected = current === value;
  const toneMap = {
    emerald: selected ? 'border-emerald-400 bg-emerald-500/10 text-emerald-100' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600',
    rose: selected ? 'border-rose-400 bg-rose-500/10 text-rose-100' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600',
    amber: selected ? 'border-amber-400 bg-amber-500/10 text-amber-100' : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-600',
  };
  return (
    <label
      className={`block cursor-pointer rounded-lg border-2 px-4 py-3 transition-all ${toneMap[tone]}`}
      data-testid={testId}
    >
      <input
        type="radio"
        className="sr-only"
        name={name}
        value={value}
        checked={selected}
        onChange={() => onChange(value)}
      />
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs opacity-70 mt-0.5">{hint}</div>}
    </label>
  );
}

// ————————————————————————————————————————
// Thank-you view (post-submit)
// ————————————————————————————————————————
function ThankYou() {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24 space-y-16" data-testid="opinion-thank-you">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
          ¡Gracias por tu opinión! 🙏
        </h1>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Cada respuesta nos ayuda a construir la mejor plataforma para conectar personas con adicciones y sus familias con centros como el tuyo. Tu tiempo lo agradecemos.
        </p>
      </div>

      {/* Estadísticas del sitio */}
      <section>
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-3">
            SinAdicciones en cifras
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Un ecosistema en crecimiento</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-center" data-testid="stat-visitas">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 mb-3">
              <TrendingUp className="w-5 h-5 text-blue-300" />
            </div>
            <div className="text-3xl font-bold text-white">+5.000</div>
            <div className="text-xs text-slate-400 mt-1">Visitas mensuales</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-center" data-testid="stat-seguidores">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-pink-500/10 border border-pink-500/30 mb-3">
              <Users className="w-5 h-5 text-pink-300" />
            </div>
            <div className="text-3xl font-bold text-white">+80.000</div>
            <div className="text-xs text-slate-400 mt-1">Seguidores en redes sociales</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-center" data-testid="stat-suscriptores">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 mb-3">
              <Mail className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="text-3xl font-bold text-white">+2.000</div>
            <div className="text-xs text-slate-400 mt-1">Suscriptores newsletter</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 text-center" data-testid="stat-prensa">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-3">
              <Newspaper className="w-5 h-5 text-amber-300" />
            </div>
            <div className="text-3xl font-bold text-white">3</div>
            <div className="text-xs text-slate-400 mt-1">Apariciones en prensa</div>
          </div>
        </div>
      </section>

      {/* Prensa */}
      <section data-testid="opinion-prensa">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-3">
            Nos han cubierto
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Apariciones en prensa</h2>
        </div>
        <MediaCoverage />
      </section>

      {/* Planes para centros */}
      <section data-testid="opinion-planes">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-semibold uppercase tracking-wider mb-3">
            Planes para centros
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-3">Recibe más cotizaciones personalizadas</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">Elige el plan que se ajusta a tus necesidades. Prioridad en resultados, badge destacado y estadísticas de conversión.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {CENTER_PLANS.map((p) => (
            <div
              key={p.id}
              className={`relative rounded-xl border p-6 ${p.popular ? 'border-amber-400/60 bg-amber-500/5' : 'border-slate-800 bg-slate-900/60'}`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.popular && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-amber-400 text-slate-950 text-[10px] font-bold uppercase tracking-wider">
                  Más popular
                </div>
              )}
              <div className="text-lg font-semibold text-white mb-1">{p.name}</div>
              <div className="text-xs text-slate-400 mb-4">{p.position}</div>
              <div className="text-3xl font-bold text-white">${p.price.toLocaleString('es-CL')}</div>
              <div className="text-xs text-slate-500 mb-5">CLP · mensual</div>
              <Button asChild className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-700">
                <Link to={`/planes?plan=${p.id}&source=opinion`} data-testid={`plan-cta-${p.id}`}>
                  Ver plan <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
            <Link to="/planes" data-testid="ver-todos-planes">Ver comparativa completa de planes →</Link>
          </Button>
        </div>
      </section>

      {/* Cierre */}
      <div className="text-center pt-6 border-t border-slate-800">
        <p className="text-sm text-slate-400">
          ¿Prefieres hablar con alguien? Escríbenos a{' '}
          <a href="mailto:contacto@sinadicciones.cl" className="text-emerald-300 hover:underline">contacto@sinadicciones.cl</a>
        </p>
      </div>
    </div>
  );
}

// ————————————————————————————————————————
// Página principal
// ————————————————————————————————————————
export default function OpinionCentrosPage() {
  const [form, setForm] = useState({
    email: '', telefono: '', nombre_centro: '', region: '',
    modalidad: [],
    recibio_cotizaciones: '', cantidad_cotizaciones_30d: '',
    calidad_cotizaciones: '', concreto_clientes: '', cantidad_clientes: '',
    como_encontro: '', nps: null,
    dispuesto_pagar_plan: '', plan_preferido: '',
    opinion: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleModalidad = (m) => {
    setForm((f) => {
      const has = f.modalidad.includes(m);
      return { ...f, modalidad: has ? f.modalidad.filter(x => x !== m) : [...f.modalidad, m] };
    });
  };

  const canSubmit = useMemo(() =>
    Boolean(form.email && form.nombre_centro && form.email.includes('@'))
  , [form.email, form.nombre_centro]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      };
      const { data } = await axios.post(`${API}/opinion-centros/submit`, payload, { timeout: 20000 });
      if (data?.success) {
        // Analytics
        try {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'opinion_centro_submit', {
              event_category: 'survey',
              plan_preferido: form.plan_preferido || 'none',
              dispuesto_pagar: form.dispuesto_pagar_plan || 'none',
              nps: form.nps,
            });
          }
        } catch (_gtagErr) { /* noop */ }
        setDone(true);
        // Scroll top al mostrar thank you
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_scrollErr) { /* noop */ }
      } else {
        setError('Hubo un problema. Inténtalo nuevamente.');
      }
    } catch (err) {
      console.error(err);
      setError('No pudimos guardar tu respuesta. Escríbenos a contacto@sinadicciones.cl');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <title>Encuesta de opinión · Centros de rehabilitación | SinAdicciones.org</title>
      <meta name="description" content="Cuéntanos qué opinas del servicio. Encuesta interna, tarda 2 minutos." />
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href="https://sinadicciones.org/opinion-centros" />

      <Header />

      {done ? (
        <ThankYou />
      ) : (
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
          {/* Hero */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-4">
              <Heart className="w-3.5 h-3.5" /> Encuesta privada · 2 min
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4 leading-[1.1]">
              Cuéntanos qué opinas de <span className="text-emerald-400">SinAdicciones</span> ✨
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Somos un proyecto pequeño que quiere mejorar. Tu opinión honesta nos ayuda a construir la mejor plataforma para conectar centros con familias que buscan tratamiento.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
              <Shield className="w-3.5 h-3.5" /> Tu respuesta es privada — no se publica ni se comparte.
            </div>
          </div>

          <form onSubmit={submit} className="space-y-8" data-testid="opinion-form">
            {/* SECCIÓN 1 — Sobre tu centro */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 md:p-8 space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center">1</span>
                Sobre tu centro
              </h2>

              <div>
                <Label htmlFor="op-email" className="text-slate-200 text-sm">Email <span className="text-rose-400">*</span></Label>
                <Input
                  id="op-email" type="email" required
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                  placeholder="contacto@tucentro.cl"
                  className="mt-1.5 bg-slate-950 border-slate-700 text-white"
                  data-testid="op-email"
                />
              </div>

              <div>
                <Label htmlFor="op-tel" className="text-slate-200 text-sm">Teléfono / WhatsApp (opcional)</Label>
                <Input
                  id="op-tel" type="tel"
                  value={form.telefono}
                  onChange={(e) => update({ telefono: e.target.value })}
                  placeholder="+56 9 1234 5678"
                  className="mt-1.5 bg-slate-950 border-slate-700 text-white"
                  data-testid="op-telefono"
                />
              </div>

              <div>
                <Label htmlFor="op-centro" className="text-slate-200 text-sm">Nombre del centro <span className="text-rose-400">*</span></Label>
                <Input
                  id="op-centro" required
                  value={form.nombre_centro}
                  onChange={(e) => update({ nombre_centro: e.target.value })}
                  placeholder="Ej: Centro Renacer, Fundación Vive Libre..."
                  className="mt-1.5 bg-slate-950 border-slate-700 text-white"
                  data-testid="op-nombre-centro"
                />
              </div>

              <div>
                <Label htmlFor="op-region" className="text-slate-200 text-sm">Región donde operas</Label>
                <select
                  id="op-region"
                  value={form.region}
                  onChange={(e) => update({ region: e.target.value })}
                  className="mt-1.5 w-full rounded-md bg-slate-950 border border-slate-700 text-white px-3 py-2 text-sm"
                  data-testid="op-region"
                >
                  <option value="">Selecciona una región</option>
                  {REGIONES_CL.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-slate-200 text-sm mb-2 block">Modalidad que ofrecen</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['residencial', 'ambulatorio', 'online'].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleModalidad(m)}
                      className={`rounded-md px-3 py-2 text-sm font-medium border-2 transition-colors ${form.modalidad.includes(m) ? 'border-emerald-400 bg-emerald-500/10 text-emerald-100' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600'}`}
                      data-testid={`op-modalidad-${m}`}
                    >
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* SECCIÓN 2 — Tu experiencia con nosotros */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 md:p-8 space-y-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center">2</span>
                Tu experiencia con SinAdicciones
              </h2>

              <div>
                <Label className="text-slate-200 text-sm mb-2 block">¿Has recibido cotizaciones desde SinAdicciones?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <RadioCard name="recibio" value="si" current={form.recibio_cotizaciones} onChange={(v) => update({ recibio_cotizaciones: v })} label="Sí, he recibido" testId="op-recibio-si" />
                  <RadioCard name="recibio" value="no" current={form.recibio_cotizaciones} onChange={(v) => update({ recibio_cotizaciones: v })} label="No, aún no" tone="rose" testId="op-recibio-no" />
                </div>
              </div>

              {form.recibio_cotizaciones === 'si' && (
                <>
                  <div>
                    <Label className="text-slate-200 text-sm mb-2 block">¿Cuántas cotizaciones aproximadamente en los últimos 30 días?</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {['0', '1-3', '4-10', '10+'].map(r => (
                        <RadioCard key={r} name="cant30d" value={r} current={form.cantidad_cotizaciones_30d} onChange={(v) => update({ cantidad_cotizaciones_30d: v })} label={r} testId={`op-cant30d-${r.replace('+','plus')}`} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-200 text-sm mb-2 block">¿Cómo calificarías la calidad de las cotizaciones?</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <RadioCard name="calidad" value="mala" current={form.calidad_cotizaciones} onChange={(v) => update({ calidad_cotizaciones: v })} label="😕 Mala" tone="rose" testId="op-calidad-mala" />
                      <RadioCard name="calidad" value="neutra" current={form.calidad_cotizaciones} onChange={(v) => update({ calidad_cotizaciones: v })} label="😐 Neutra" tone="amber" testId="op-calidad-neutra" />
                      <RadioCard name="calidad" value="buena" current={form.calidad_cotizaciones} onChange={(v) => update({ calidad_cotizaciones: v })} label="🙂 Buena" testId="op-calidad-buena" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-200 text-sm mb-2 block">¿Has concretado clientes desde estas cotizaciones?</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <RadioCard name="concreto" value="si" current={form.concreto_clientes} onChange={(v) => update({ concreto_clientes: v })} label="Sí, hemos cerrado" testId="op-concreto-si" />
                      <RadioCard name="concreto" value="no" current={form.concreto_clientes} onChange={(v) => update({ concreto_clientes: v })} label="Aún no" tone="rose" testId="op-concreto-no" />
                    </div>
                  </div>

                  {form.concreto_clientes === 'si' && (
                    <div>
                      <Label className="text-slate-200 text-sm mb-2 block">Aproximadamente, ¿cuántos clientes has cerrado desde SinAdicciones?</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {['1-2', '3-5', '6-10', '10+'].map(r => (
                          <RadioCard key={r} name="cantcli" value={r} current={form.cantidad_clientes} onChange={(v) => update({ cantidad_clientes: v })} label={r} testId={`op-cantcli-${r.replace('+','plus')}`} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div>
                <Label className="text-slate-200 text-sm mb-2 block">¿Cómo conociste SinAdicciones?</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    { v: 'google', label: 'Google' },
                    { v: 'rrss', label: 'Redes sociales' },
                    { v: 'prensa', label: 'Prensa' },
                    { v: 'recomendacion', label: 'Recomendación' },
                    { v: 'otro', label: 'Otro' },
                  ].map(o => (
                    <RadioCard key={o.v} name="como" value={o.v} current={form.como_encontro} onChange={(v) => update({ como_encontro: v })} label={o.label} testId={`op-como-${o.v}`} />
                  ))}
                </div>
              </div>

              {/* NPS */}
              <div>
                <Label className="text-slate-200 text-sm mb-2 block flex items-center gap-2">
                  <Star className="w-3.5 h-3.5 text-amber-300" />
                  Del 0 al 10, ¿qué tan probable es que recomiendes SinAdicciones a otro centro?
                </Label>
                <div className="grid grid-cols-11 gap-1">
                  {Array.from({ length: 11 }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => update({ nps: i })}
                      className={`aspect-square rounded-md border-2 text-sm font-semibold transition-all ${form.nps === i ? 'border-emerald-400 bg-emerald-500/15 text-white' : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-600'}`}
                      data-testid={`op-nps-${i}`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                  <span>Nada probable</span>
                  <span>Muy probable</span>
                </div>
              </div>
            </section>

            {/* SECCIÓN 3 — Intención de pago */}
            <section className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-slate-900/50 p-6 md:p-8 space-y-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center">3</span>
                <Sparkles className="w-4 h-4 text-emerald-300" />
                Plan mensual (opcional)
              </h2>

              <div>
                <Label className="text-slate-200 text-sm mb-2 block">
                  ¿Estarías dispuesto a pagar un plan mensual para asegurar más clientes?
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <RadioCard name="pagar" value="si" current={form.dispuesto_pagar_plan} onChange={(v) => update({ dispuesto_pagar_plan: v })} label="Sí, me interesa" testId="op-pagar-si" />
                  <RadioCard name="pagar" value="quizas" current={form.dispuesto_pagar_plan} onChange={(v) => update({ dispuesto_pagar_plan: v })} label="Quizás" tone="amber" testId="op-pagar-quizas" />
                  <RadioCard name="pagar" value="no" current={form.dispuesto_pagar_plan} onChange={(v) => update({ dispuesto_pagar_plan: v })} label="No por ahora" tone="rose" testId="op-pagar-no" />
                </div>
              </div>

              {(form.dispuesto_pagar_plan === 'si' || form.dispuesto_pagar_plan === 'quizas') && (
                <div>
                  <Label className="text-slate-200 text-sm mb-2 block">¿Qué plan se ajusta mejor a tus necesidades?</Label>
                  <div className="grid md:grid-cols-3 gap-2">
                    {CENTER_PLANS.map((p) => (
                      <label
                        key={p.id}
                        className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all ${form.plan_preferido === p.id ? 'border-emerald-400 bg-emerald-500/10' : 'border-slate-700 bg-slate-950 hover:border-slate-600'}`}
                        data-testid={`op-plan-${p.id}`}
                      >
                        <input
                          type="radio"
                          className="sr-only"
                          name="plan"
                          value={p.id}
                          checked={form.plan_preferido === p.id}
                          onChange={() => update({ plan_preferido: p.id })}
                        />
                        {p.popular && (
                          <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 text-[10px] font-bold uppercase tracking-wider">
                            Más popular
                          </div>
                        )}
                        <div className="text-sm font-semibold text-white">{p.name}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{p.position}</div>
                        <div className="text-xl font-bold text-emerald-300 mt-2">${p.price.toLocaleString('es-CL')}<span className="text-xs text-slate-500 font-normal">/mes</span></div>
                      </label>
                    ))}
                    <label
                      className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${form.plan_preferido === 'ninguno' ? 'border-slate-400 bg-slate-800/40' : 'border-slate-700 bg-slate-950 hover:border-slate-600'} md:col-span-3`}
                      data-testid="op-plan-ninguno"
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name="plan"
                        value="ninguno"
                        checked={form.plan_preferido === 'ninguno'}
                        onChange={() => update({ plan_preferido: 'ninguno' })}
                      />
                      <div className="text-sm text-slate-300">Ninguno de los anteriores / me gustaría ver otras opciones</div>
                    </label>
                  </div>
                </div>
              )}
            </section>

            {/* SECCIÓN 4 — Opinión libre */}
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 md:p-8 space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center">4</span>
                Tu recomendación
              </h2>
              <div>
                <Label htmlFor="op-opinion" className="text-slate-200 text-sm">
                  ¿Qué mejorarías? ¿Qué te ha gustado? Escríbenos libremente
                </Label>
                <Textarea
                  id="op-opinion"
                  value={form.opinion}
                  onChange={(e) => update({ opinion: e.target.value })}
                  rows={5}
                  maxLength={4000}
                  placeholder="Cuéntanos con tus palabras. Cualquier cosa que consideres importante..."
                  className="mt-1.5 bg-slate-950 border-slate-700 text-white resize-none"
                  data-testid="op-opinion"
                />
                <div className="text-[11px] text-slate-500 mt-1 text-right">{form.opinion.length}/4000</div>
              </div>
            </section>

            {/* Submit */}
            {error && (
              <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" data-testid="op-error">{error}</div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-full disabled:opacity-50"
                data-testid="op-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Enviando...
                  </>
                ) : (
                  <>
                    Enviar mi opinión <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500">
                <Phone className="w-3 h-3 inline mr-1" />
                ¿Prefieres una llamada? <a href="mailto:contacto@sinadicciones.cl" className="text-emerald-300 hover:underline">contacto@sinadicciones.cl</a>
              </p>
            </div>
          </form>
        </main>
      )}

      <Footer />
    </div>
  );
}
